import Box from '@mui/material/Box';
import MuiLink from '@mui/material/Link';
import { Theme } from '@mui/material/styles';
import React from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { ColumnDef } from './ServiceTable.types';
import { SlaCell } from './SlaCell';
import { SlaStatusCell } from './SlaStatusCell';
import { formatFriendlyDateTime } from '../../../utils/common';
import { LogoWithFallback } from '../../atoms/LogoWithFallback';
import { NotesCell } from '../../atoms/NotesCell';
import { TablePill, toneForPriority, toneForStatus } from '../../atoms/TablePill';
import { toTagCellItems } from '../../molecules/TagCell';
import { TagChips } from '../../molecules/TagChips';

const NOTES_FIELD_KEYS: ReadonlySet<string> = new Set([
  'creationNotes',
  'lastUpdatedNote',
  'allNotes',
  'structureNodeNotes',
  'billingNotes',
]);

// Service-row fields that hold ISO timestamps and should render as
// "8 May 2026, 1:13 AM" instead of the raw ISO string.
const DATE_FIELD_KEYS: ReadonlySet<string> = new Set([
  'createdAt',
  'updatedAt',
  'closedAt',
  'scheduledDate',
  'followUpDate',
  'specialScheduled',
  'firstTimeOnSite',
]);

const INLINE_LINK_SX = {
  color: 'v4.accent',
  textDecoration: 'none',
  '&:hover': { textDecoration: 'underline' },
} as const;

const renderNodeLink = (text: string, nodeId: number | undefined, canLink: boolean) => {
  if (!text) return text;
  if (!canLink || !nodeId) return text;
  return (
    <MuiLink
      component={RouterLink}
      to={`/structure/${nodeId}`}
      onClick={(e: React.MouseEvent) => e.stopPropagation()}
      sx={INLINE_LINK_SX}
    >
      {text}
    </MuiLink>
  );
};

/**
 * Render the contents of a single service-table cell for the given column.
 * Extracted from ServiceTableRow as a standalone dispatch so the row component
 * stays within the file-size limit; behaviour is unchanged.
 *
 * @param canViewStructure Render Location Name / Primary Allocation as plain
 *   text when false, so restricted users (clients, partners) see the value
 *   without a hyperlink that would 404 or hit a permission error.
 */
export const renderServiceCell = (
  col: ColumnDef,
  row: Record<string, unknown>,
  theme: Theme,
  canViewStructure: boolean
): React.ReactNode => {
  if (col.render) return col.render(row);

  const serviceId = row.serviceId as number | undefined;
  const value = row[col.key];

  if (col.key === 'serviceId' && serviceId != null) {
    return (
      <MuiLink
        component={RouterLink}
        to={`/services/${serviceId}`}
        onClick={(e: React.MouseEvent) => e.stopPropagation()}
        sx={{ color: theme.palette.v4.accent, fontWeight: 600, textDecoration: 'none' }}
      >
        {String(serviceId)}
      </MuiLink>
    );
  }

  // Status and priority both render as the shared table pill, the same one the
  // other list tables use — rectangular, and measured for contrast.
  if (col.key === 'serviceStatusName' && typeof value === 'string') {
    return (
      <TablePill tone={toneForStatus(value)} ariaLabel={`Status: ${value}`}>
        {value}
      </TablePill>
    );
  }

  if (col.key === 'priorityCode' && typeof value === 'string') {
    // The colour configured per client/contract picks the tone; the raw hex does
    // not reach the screen. Uppercase because the codes are not consistently
    // cased in the data (Hso, Sup, P2, SCH).
    const priorityObj = row.priority as Record<string, unknown> | undefined;
    const configured = typeof priorityObj?.color === 'string' ? priorityObj.color : undefined;
    return (
      <TablePill tone={toneForPriority(value, configured)} ariaLabel={`Priority: ${value}`}>
        {value.toUpperCase()}
      </TablePill>
    );
  }

  if (col.key === 'clientSLASummary' || col.key === 'opsSLASummary') {
    return <SlaCell value={value} />;
  }

  // Derived status columns read from the underlying SLA summary — the BFF
  // payload contains clientSLASummary / opsSLASummary, not a separate status.
  if (col.key === 'clientSlaStatus') {
    return <SlaStatusCell value={row.clientSLASummary} />;
  }
  if (col.key === 'opsSlaStatus') {
    return <SlaStatusCell value={row.opsSLASummary} />;
  }

  // Contract logo: render as image, with the client/contract name as tooltip + alt text
  if (col.key === 'logoPic') {
    const src = typeof value === 'string' ? value : '';
    if (!src) return '';
    const label =
      (row.contractName as string | undefined) ||
      (row.clientHierarchy as string | undefined) ||
      (row.clientName as string | undefined) ||
      'Contract logo';
    return <LogoWithFallback src={src} code={row.clientCode as string | undefined} label={label} />;
  }

  // Primary allocation: red "Unallocated" text when empty, otherwise a hyperlink.
  // TEMP: target is /services until the BFF returns primaryAllocationNodeId — then
  // swap back to renderNodeLink(text, row.primaryAllocationNodeId as number | undefined, canViewStructure).
  if (col.key === 'primaryAllocation') {
    const text = value != null ? String(value).trim() : '';
    if (!text) {
      return (
        <Box component="span" sx={{ fontSize: '11px', color: 'error.main' }}>
          Unallocated
        </Box>
      );
    }
    if (!canViewStructure) return text;
    return (
      <MuiLink
        component={RouterLink}
        to="/services"
        onClick={(e: React.MouseEvent) => e.stopPropagation()}
        sx={INLINE_LINK_SX}
      >
        {text}
      </MuiLink>
    );
  }

  // Location: hyperlink to the structure node profile when an id is available
  if (col.key === 'locationName') {
    const text = value != null ? String(value) : '';
    return renderNodeLink(text, row.structureNodeId as number | undefined, canViewStructure);
  }

  // The "Parts Pending" picker toggle ships to the BFF as `partsPending`, but
  // the response carries the aggregated pending-parts list under `pendedParts`
  // — the column key and the row field intentionally differ, so map it
  // explicitly here (reading row[col.key] would always be undefined and render
  // blank). NotesCell truncates long part lists with a tooltip and sanitises
  // the value; an empty list renders nothing.
  if (col.key === 'partsPending') {
    return <NotesCell value={row.pendedParts} maxWidth={col.maxWidth ?? 200} />;
  }

  // The list endpoint aggregates tags into a JSON string; without this the
  // fallback below prints that JSON verbatim into the cell.
  if (col.key === 'tags') {
    const items = toTagCellItems(value);
    return items.length > 0 ? <TagChips tags={items} /> : '';
  }

  if (NOTES_FIELD_KEYS.has(col.key)) {
    return <NotesCell value={value} maxWidth={col.maxWidth ?? 180} />;
  }

  if (DATE_FIELD_KEYS.has(col.key)) {
    return formatFriendlyDateTime(
      typeof value === 'string' || value instanceof Date ? value : null
    );
  }

  return value != null ? String(value) : '';
};
